/*
 * IcInventurApp.tsx - Inventur-Bezogene Funktionalitäten.
 *
 * Created by Dr. Maximillian Dornseif 2022-12-29 in huWaWi3 31.12.0
 * Copyright (c) 2022, 2023 HUDORA GmbH
 */

import { useTitle } from 'fluentui-hooks'
import gql from 'graphql-tag'
import React from 'react'
import { Link } from 'wouter'

import { KindPage3 } from '../lib/components/ke/KindPage3'
import { IcInventurHochladen } from './IcInventurHochladen'
import { IcInventurZaehlbedarf } from './IcInventurZaehlbedarf'

export const INVENTUR_NAME = 'inventur_2023'

export const getIinventurSidebarLinks = () => {
  useTitle('Inventur - Überblick')
  return [
    {
      title: 'Start',
      path: '/ic/inventur/start',
      component: <IcInventurStart />,
    },
    {
      title: 'Zählbedarf',
      path: '/ic/inventur/zaehlbedarf',
      component: <IcInventurZaehlbedarf />,
    },
    {
      title: 'Inventur-Bestätigungen',
      path: '/ic/inventur/bestaetigungen',
      component: (
        <div className="authEnforced">
          <KindPage3
            basePath={'/ic/inventur'}
            componentPath={'bestaetigungen'}
            schemaName={'HwIcIntventurBestaetigung'}
            title={'Inventur-Bestätigungen'}
            listQuery={INVENTURBESTAETIGUNG_LIST_QUERY}
            entityQuery={INVENTURBESTAETIGUNG_ENTITY_QUERY}
          />
        </div>
      ),
    },
    {
      title: '- offen',
      path: '/ic/inventur/bestaetigungenOffen',
      component: (
        <div className="authEnforced">
          <KindPage3
            basePath={'/ic/inventur'}
            componentPath={'bestaetigungenOffen'}
            schemaName={'HwIcIntventurBestaetigung'}
            title={'Offene Inventur Bestätigungen'}
            listQuery={INVENTUROFFEN_LIST_QUERY}
            entityQuery={INVENTURBESTAETIGUNG_ENTITY_QUERY}
          />
        </div>
      ),
    },
    {
      title: '- hochladen',
      path: '/ic/inventur/hochladen',
      component: (
        <div className="authEnforced">
          <IcInventurHochladen />
        </div>
      ),
    },
    {
      title: '- verbuchen',
      path: '/ic/inventur/verbuchen',
      component: <div className="authEnforced">Das fehlt noch.</div>,
    },
    {
      title: '(Vorbereitungen)',
      path: '/ic/inventur/vorbereitungen',
      component: <IcInventurVorbereitungen />,
    },
  ]
}

export const IcInventurStart = () => {
  useTitle(`Jahresinventur`)

  return (
    <div>
      <h1>Jahresinventur</h1>
      <section>
        <h2>Vorraussetzungen</h2>
        <p>
          Grundsätzlich kann die Inventur immer und und auch für einzelne SKUs und Läger durchgeführt werden.
          <em>
            Allerdings muss sichergestellt werden, das zwischen der Zählung (und Erstellung der
            "InventurBestätigung") und keine Lagerbewegungen stattfinden.
          </em>{' '}
          Das bedeutet, keine Warenvereinnamungen, Komissionierungen, Korrekturbuchungen.
          <br />
          In dieser weiteren Dokumentation wird davon ausgegangen, dass die Inventur für das alle Läger und
          alle SKUs durchgeführt wird.
        </p>
        <p>
          Eine solche Jahresinventur hat immer einen Namen. Aktuell <code>{INVENTUR_NAME}</code>. Der Name ist
          in dem <Link href="/ic/config/SYSCONF1">Konfigurations-Schlüssel</Link> <code>inventur_name</code>
          gespeichert und sollte vor dem Beginn der Inventur geändert werden.
        </p>
      </section>
      <section>
        <h2>Vorbereitungen</h2>

        <p>
          Es gibt eine Vielzahl von sinnvollen Bereinigungen und Korrekturen, die vor der Inventur
          durchgeführt werden sollten. Diese haben nichts mit der eigentlichen Feststellung der Lagermenge zu
          tun und werden an dieser Stelle behandelt. Das Programm{' '}
          <code>huwawi-cli lager inventur vorbereiten</code> und die Rubrik{' '}
          <Link href={'/ic/inventur/vorbereitungen'}>Vorbereitungen</Link> hat einige Tipps und Hinweise.
        </p>
      </section>
      <section>
        <h2>Zählbedarf ermitteln / Zähllisten erstellen</h2>

        <p>
          Das System erwartet, das alle (SKU, Lager) Paare, die im vergangenen Jahr Bestand hatten, in der
          Inventur gezählt werden.
          <br />
          Um die Zähllisten zu erstellen wird BigQuery genutzt. Dadurch werden nur die Daten bis zum Vortag
          berücksichtigt. Die entsprechende Anzeige findet sich unter{' '}
          <Link href={'/ic/inventur/zaehlbedarf'}>Zählbedarf</Link>.
        </p>
        <p>
          Hier erzeugt man die (zunächst leeren) Inventur Bestätigungen. Diese Verwalten die Zählergebnisse
          und die Verbuchung der selben. Rechts sieht man die <code>(Lager, SKU)</code> Paare, die gezählt
          werden sollte. Rechts die Liste der schon erzeugten Inventur Bestätigungen. Mit dem Button in der
          Mitte können die fehlenden Inventur Bestätigungen erzeugt werden. Diese Funktion kann problemlos
          mehrfach ausgeführt werden.{' '}
          <a href="http://f.foxel.org/Screen-Recording-2023-01-01-21-38-17.mp4">Dieses Video</a> illustriert
          den Ablauf.
        </p>

        <p>
          Der ganze weitere Verlauf dreht sich um Inventur Bestätigungen. Zunächst werden die gezählten Mengen
          darin gespeichert, dann die entsprechenden Buchungen vorgenommen.
        </p>
      </section>
      <section>
        <h2>Zähllisten hochladen</h2>
        <p>
          Es wird gezählt, was wirklich am Lager vorhanden ist. Die Zähllisten werden dann aus einer
          Tabellenkalkulation in huWaWi3 hochgeladen, um die entsprechenden Inventur Bestätigungen zu
          aktualisieren.
          <br />
          Die <Link href={`/ic/inventur/hochladen`}>Hochlade-Seite</Link> zeigt links die Einträge, für die
          Keine Zählung vorhanden ist. Daneben kann man die Ergebnisse der Zählung direkt aus der
          Tabellenkalkulation in das Fenster kopieren.{' '}
          <a href="http://f.foxel.org/Screen-Shot-2022-12-31-15-09-14.09.png">Hier kann man Beispieldaten</a>{' '}
          sehen.
          <br />
          Durch betätigen der Schaltfläche <b>Zählergebnisse speichern</b> werden die übernahme in die
          Datenbank gestartet.
          <br />
          Ganz rechts die Einträge, für die bereits eine Zählung in der Datenbank ist. Dir Liste aktualisiert
          sich immer nur beim neu Laden der Seite. <br />
          Ein Video über den Vorgang gibt es{' '}
          <a href="http://f.foxel.org/Screen-Recording-2022-12-31-15-24-24.mp4">hier</a>. Wenn auf der{' '}
          <Link href={`/ic/inventur/hochladen`}>Hochlade-Seite</Link> unter "1. Noch ungezählt: 0 Einträge"
          steht, ist die Zählung abgeschlossen und es kann mit der Verbuchung begonnen werden.
        </p>
      </section>
      <section>
        <h2>Verbuchung der Inventur</h2>
        <p>
          Nun geht es darum, die Buchbestände auf die tatsächlich gezählten Bestände zu bringen. Dazu werden
          je nach Bedarf ein oder Zwei Einträge im Bestandsbuch erstellt:
          <ol>
            <li>
              Bei SKUs, wo gezählter Bestand und Buchbestand nicht identisch sind, wird eine Korrekturbuchung
              erzeugt, die den Buchbestand auf den Sollbestand bringt (Beispiel{' '}
              <Link href={'/ic/bestandsbuch/LB18247430036/'}>LB18247430036</Link> oder{' '}
              <a href="http://f.foxel.org/Screen-Shot-2023-01-01-23-38-58.33.png">diese Liste</a>). Diese
              Buchungen werden immer mit der PosNr <code>-5</code> erzeugt, so dass sie leicht in Auswertungen
              des Bestandsbuchs herausgefiltert werden können.
            </li>
            <li>
              Bei allen SKUs wird ein Bestandsbucheintrag erzeugt, der die Inventur und die gezählte Menge
              dokumentiert. Dies ist besonders bei Permanenter Inventur wichtig, um die Zählung einzeln pro
              Artikel dokumentieren zu können (Beispiel{' '}
              <Link href={'/ic/bestandsbuch/LB52881978708459523/'}>LB52881978708459523</Link> oder{' '}
              <a href="http://f.foxel.org/Screen-Shot-2023-01-01-23-40-08.18.png">diese Liste</a>). Diese
              Buchungen werden immer mit der PosNr <code>-6</code> erzeugt, so dass sie leicht in Auswertungen
              des Bestandsbuchs herausgefiltert werden können.
            </li>
          </ol>
        </p>

        <p>Diese Buchungsschritte können bislang nur an der Kommandozeile ausgeführt werden:</p>
        <pre>
          <code>
            {`$ pipenv run python -m components.inventur.buchen korrektur
$ pipenv run python -m components.bestandsbuch.ic_bestandsbuch -e 1000 # ca 20 Minuten
$ pipenv run python -m components.inventur.buchen bestaetigung # (Wenn die automatische Verarbeitung abgeschaltet wurde)
$ pipenv run python -m components.bestandsbuch.ic_bestandsbuch -e 1000 # ca 20 Minuten
$ pipenv run python -m components.inventur.buchen bestaetigung # (Wenn die automatische Verarbeitung abgeschaltet wurde)
`}
          </code>
        </pre>

        <p>
          Ergebnisse befinden sich im Inventurordner. Der Inventurordner wird mit der Jahreszahl automatisch
          in gs://hudora-abt-fibu/inventur (Google Clound Storage) erstellt.
        </p>
      </section>
      <section>
        <h2>Überprüfung der Inventur</h2>
        <ol>
          <li>
            Es sollte keine <Link href="/ic/inventur/bestaetigungenOffen">offen Inventurbestätigungen</Link>{' '}
            mehr geben.
          </li>
          <li>
            Es sollte kein{' '}
            <Link href="/ic/unverbuchtesBestandsbuch">unverbuchtes Bestandsbuch mehr geben</Link>{' '}
          </li>
        </ol>
      </section>
    </div>
  )
}

export const IcInventurVorbereitungen = () => {
  useTitle('Inventur - mögliche Vorbereitungen')
  return (
    <div>
      <h1>Vorbereitungen zur Jahresinventur</h1>
      <section>
        <h2>Inventurstörungen vermeiden</h2>

        <p>Es sollte keine Komissionierung Stattfinden.</p>
        <ol>
          <li>
            Die <Link href={'/ic/kommi'}>Liste Offene Kommi Aufträge</Link> sollte leer sein.
          </li>
          <li>
            Unter{' '}
            <a href="https://hulogi-eu.appspot.com/admin/" rel="nofollow">
              https://hulogi-eu.appspot.com/admin/
            </a>{' '}
            sollten die Kommiauftrag Queue leer sein.
          </li>
          <li>
            Unter{' '}
            <a href="https://hulogi-eu.appspot.com/admin/" rel="nofollow">
              https://hulogi-eu.appspot.com/admin/
            </a>{' '}
            keine Unverarbeiteten Kommiaufträge.
          </li>
          <li>
            Unter{' '}
            <a href="https://hulogi-eu.appspot.com/admin/" rel="nofollow">
              https://hulogi-eu.appspot.com/admin/
            </a>{' '}
            keine Unverarbeiteten Rückmeldungen geben.
          </li>
          <li>
            Zuteilung stoppen, so dass keine neuen Belege erstellt werden. Das kann man tun, in dem man in der
            <a
              href="https://huwawi3.hudora.de/wawi/config/SYSCONF1?datensatzBearbeitenPanel=1"
              rel="nofollow"
            >
              huWaWi-Konfiguration
            </a>{' '}
            <code>zuteilung_aktiv=false</code> setzt.
          </li>
        </ol>

        <p>
          Dafür sorgen, dass keine Warenvereinnamungen, Komissionierungen, Korrekturbuchungen stattfinden,
          indem man mit den entsprechenden Abteilungen kommuniziert. Zusätzlich kann man in der{' '}
          <a href="https://huwawi3.hudora.de/wawi/config/SYSCONF1?datensatzBearbeitenPanel=1" rel="nofollow">
            huWaWi-Konfiguration
          </a>{' '}
          <code>inventur_aktiv=true</code> setzen, das vermeidet die gröbsten Schnitzer.
        </p>

        <h2>Vorgänge bereinigen</h2>

        <p>
          Wenn man sich entscheidet, die Auftragsverarbeitung komplett zu stoppen, kann man schauen, das man
          sicherstellt, dass es an diversen stellen keine Karteileichen gibt:{' '}
        </p>
        <ol>
          <li>
            Zuteilungen:{' '}
            <a href="https://console.cloud.google.com/datastore/entities;kind=ic_Zuteilung;ns=__$DEFAULT$__;sortCol=created_at;sortDir=DESCENDING/query/kind?project=huwawi2">
              Datastore
            </a>
            , <Link href="/ic/zuteilung">huWaWi</Link>
          </li>
          <li>
            Neuverbuchungswünsche:{' '}
            <a href="https://console.cloud.google.com/datastore/entities;kind=ic_Neuverbuchungswunsch;ns=__$DEFAULT$__;sortCol=created_at;sortDir=DESCENDING/query/kind?project=huwawi2">
              Datastore
            </a>{' '}
            <Link href="/ic/neuverbuchung">huWaWi</Link>
          </li>
          <li>
            Buchungssperren: <a href="http://huwawi.hudora.de/icui/buchungssperren/">Datastore</a>{' '}
            <Link href="/ic/icSperren">huWaWi</Link>
          </li>
          <li>
            Aufträge im Status <code>400zugeteilt</code>:{' '}
            <a href="https://console.cloud.google.com/datastore/entities;kind=ic_AuftragsKopf;ns=__$DEFAULT$__/query/kind;filter=%5B%226%2Fstatus%7CSTR%7CEQ%7C12%2F400zugeteilt%22%5D?project=huwawi2">
              Datastore
            </a>{' '}
            <Link href="/wawi/ic/a">huWaWi</Link>
          </li>
          <li>
            Kommiaufträge mit einem status kleiner <code>500lieferschein_erzeugt</code>:{' '}
            <a href="https://console.cloud.google.com/datastore/entities;kind=lg_Kommiauftrag;ns=__$DEFAULT$__/query/kind;filter=%5B%226%2Fstatus%7CSTR%7CLT%7C24%2F%20500lieferschein_erzeugt%22%5D?project=huwawi2">
              Datastore
            </a>{' '}
            <Link href="/ic/kommi">huWaWi</Link>
          </li>
          <li>
            Unverbuchte Bestandsbucheinträge:{' '}
            <a href="http://huwawi.hudora.de/icui/bestandsbuch/?unverbucht=1">Datastore</a>{' '}
            <Link href="/ic/unverbuchtesBestandsbuch">huWaWi</Link>
          </li>
        </ol>

        {/* <h2>Vorab-Zählung</h2>

        <h2>Konsistenz-Prüfungen</h2>
 */}
        {/* <li>Nun sollten die Artikelsummen aus dem Bestandsbuch neu generiert werden, um sicherzustellen, das diese dem Bestandsbuch entsprechen. <a href="https://huwawi3.hudora.de/wawi/config/SYSCONF1?datensatzBearbeitenPanel=1" rel="nofollow">Konfigurationswerte setzen</a>: <code>artikelsumme_neu_genrieren_aktiv=true</code>. Der entsprechende Generierungslauf dauert einige Minuten, danach kann <code>artikelsumme_neu_genrieren_aktiv</code> wieder auf <code>false</code> gesetzt werden, um Rechenzeit zu sparen.</li>
<li><code>python -m legacy.inventur.vorbereitung</code> erzeugt in zB <a href="https://console.cloud.google.com/storage/browser/hudora-abt-fibu/inventur/2021" rel="nofollow">gs://hudora-abt-fibu/inventur/2021</a> Liste der bebuchten Läger und Artikel. Erzeugt leere <code>ic_InventurBestaetigung</code> Datensätze. Die Funktion schreibt nicht nur eine Datei mit den zu zählenden Artikeln und erzeugt die Objekte <code>ic_InventurBestaetigung</code> für jedes Paar (Lager, ArtNr), sondern startet auch eine Konsistenzprüfung für alle Artikel, die im Inventurzeitraum Bestand hatten. Diese Funktion sollte so früh wie möglich gestartet werden, da je nach Anzahl der erzeugten Neuverbuchungswünsche die Verbuchung einige Stunden dauern kann. Für die Verbuchung der Neuverbuchungswünsche muss die entsprechende <a href="https://huwawi3.hudora.de/wawi/config/SYSCONF1?datensatzBearbeitenPanel=1" rel="nofollow">Variable gesetzt</a> werden: <code>bestandsbuch_neu_generieren_aktiv=true</code></li>
</ol> */}
      </section>
    </div>
  )
}

export const INVENTURBESTAETIGUNG_ENTITY_QUERY = gql`
  query ibLEsen($nr: ID!) {
    entity: hwIcIntventurBestaetigung(designator: $nr) {
      abweichung_menge
      abweichung_wert
      artnr
      bestaetigt
      buchungsnr_korrektur
      buchungsnr_bestaetigung
      created_at
      designator
      id
      info
      inventur
      lager
      quelle
      status
      updated_at
      verbucht
      zaehlbestand
    }
  }
`
export const INVENTURBESTAETIGUNG_LIST_QUERY = gql`
  query IntventurBestaetigungenSorted($cursor: String) {
    nodes: hwIcIntventurBestaetigungPages(
      after: $cursor
      sort: { fields: [inventur, lager, artnr], order: [DESC, ASC, ASC] }
    ) {
      edges {
        cursor
        node {
          abweichung_menge
          abweichung_wert
          artnr
          bestaetigt
          buchungsnr_korrektur
          buchungsnr_bestaetigung
          created_at
          designator
          id
          info
          inventur
          lager
          quelle
          status
          updated_at
          verbucht
          zaehlbestand
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

export const INVENTUROFFEN_LIST_QUERY = gql`
  query IntventurBestaetigung($cursor: String) {
    nodes: hwIcIntventurBestaetigungPages(
      after: $cursor
      filter: { verbucht: { eq: false } }
      sort: { fields: artnr, order: ASC }
      limit: 10000
    ) {
      edges {
        cursor
        node {
          abweichung_menge
          abweichung_wert
          artnr
          bestaetigt
          buchungsnr_korrektur
          buchungsnr_bestaetigung
          created_at
          designator
          id
          info
          inventur
          lager
          quelle
          status
          updated_at
          verbucht
          zaehlbestand
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`
